import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useConversations, type Conversation } from '@/hooks/useConversations';
import { useConversationUnreadCount, useMessages } from '@/hooks/useMessages';
import { useAuth } from '@/contexts/AuthContext';
import { useBreederProfile } from '@/hooks/useBreederProfile';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';

interface ConversationsListProps {
  onSelectConversation: (conversation: Conversation) => void;
  selectedConversationId?: string;
}

const ConversationItem = ({ conversation, isSelected, onSelect }: {
  conversation: Conversation;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { user } = useAuth();
  const isUserBreeder = user?.user_metadata?.user_type === 'breeder';
  
  // Determine the other participant based on current user
  const otherParticipantId = user?.id === conversation.buyer_id ? conversation.breeder_id : conversation.buyer_id;
  const isOtherParticipantBreeder = user?.id === conversation.buyer_id; // If current user is buyer, other is breeder
  
  const { data: unreadCount = 0 } = useConversationUnreadCount(conversation.id);
  // REMOVED: const { data: messages = [] } = useMessages(conversation.id); - This was causing multiple WebSocket subscriptions!
  
  // Fetch breeder profile if the other participant is a breeder
  const { data: breederProfile } = useBreederProfile({ 
    breederId: otherParticipantId 
  });
  
  // Fetch buyer profile if the other participant is a buyer
  const { data: buyerProfile } = useQuery({
    queryKey: ['buyer-profile', otherParticipantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image_url, business_name, user_type')
        .eq('id', otherParticipantId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!otherParticipantId,
  });
  
  // Determine display name and avatar based on the other participant's actual type
  const getDisplayName = () => {
    // Check the actual user type from the profile data
    const otherParticipantProfile = buyerProfile || breederProfile;
    
    if (buyerProfile && buyerProfile.user_type === 'buyer') {
      // Other participant is a buyer - show buyer name as "John D."
      const firstName = buyerProfile.first_name || '';
      const lastName = buyerProfile.last_name || '';
      if (firstName && lastName) {
        return `${firstName} ${lastName.charAt(0)}.`;
      } else if (firstName) {
        return firstName;
      } else if (buyerProfile.business_name) {
        return buyerProfile.business_name;
      }
      return 'Unknown Buyer';
    } else if (buyerProfile && buyerProfile.user_type === 'breeder') {
      // Other participant is actually a breeder - show business name
      return buyerProfile.business_name || `${buyerProfile.first_name || ''} ${buyerProfile.last_name || ''}`.trim() || 'Unknown Breeder';
    } else if (breederProfile) {
      // Fallback to breeder profile data
      return breederProfile.name || 'Unknown Breeder';
    }
    
    return 'Unknown User';
  };
  
  const getAvatarImage = () => {
    if (buyerProfile) {
      return buyerProfile.profile_image_url;
    } else if (breederProfile) {
      return breederProfile.profileImage;
    }
    return undefined;
  };
  
  const getAvatarFallback = () => {
    if (buyerProfile && buyerProfile.user_type === 'buyer') {
      // For buyers, use first letters of first and last name, or business name
      if (buyerProfile.first_name && buyerProfile.last_name) {
        return `${buyerProfile.first_name.charAt(0)}${buyerProfile.last_name.charAt(0)}`.toUpperCase();
      } else if (buyerProfile.first_name) {
        return buyerProfile.first_name.charAt(0).toUpperCase();
      } else if (buyerProfile.business_name) {
        return buyerProfile.business_name.charAt(0).toUpperCase();
      }
      return 'B';
    } else if (buyerProfile && buyerProfile.user_type === 'breeder') {
      // Other participant is actually a breeder
      return buyerProfile.business_name?.charAt(0).toUpperCase() || buyerProfile.first_name?.charAt(0).toUpperCase() || 'BR';
    } else if (breederProfile) {
      // For breeders, use first letter of name/business name
      return breederProfile.name?.charAt(0).toUpperCase() || 'BR';
    }
    
    return 'U';
  };

  // Get message preview - we'll show a placeholder since we don't load messages for each conversation
  const getMessagePreview = () => {
    return 'Tap to view messages';
  };

  // Get listing title or "General Question"
  const getListingTitle = () => {
    return conversation.listing_title || 'General Question';
  };

  return (
    <div 
      className={`p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${
        isSelected ? 'bg-green-50 border-green-200' : 'bg-green-50 hover:bg-green-100'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start space-x-3">
        <div className="relative flex-shrink-0">
          <Avatar className="w-10 h-10 sm:w-12 sm:h-12">
            <AvatarImage src={getAvatarImage()} />
            <AvatarFallback className="bg-green-700 text-white font-medium text-xs sm:text-sm">
              {getAvatarFallback()}
            </AvatarFallback>
          </Avatar>
          {unreadCount > 0 && (
            <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-purple-500 rounded-full"></div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-1">
            <div className="font-semibold text-gray-900 truncate text-xs sm:text-sm pr-2">
              {getListingTitle()}
            </div>
            <div className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
              {formatDistanceToNow(new Date(conversation.updated_at), { addSuffix: true })}
            </div>
          </div>
          
          <div className="font-medium text-gray-700 text-xs sm:text-sm mb-1 truncate">
            {getDisplayName()}
          </div>
          
          <div className="text-gray-600 text-xs sm:text-sm truncate">
            {getMessagePreview()}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ConversationsList = ({ onSelectConversation, selectedConversationId }: ConversationsListProps) => {
  const { user } = useAuth();
  const { data: conversations = [], isLoading } = useConversations();
  const queryClient = useQueryClient();

  // Removed competing real-time subscription - messages hook handles real-time updates

  // Show ALL conversations - including new ones without messages yet
  const conversationsWithMessages = conversations;

  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col">
        <Card className="flex-1 flex flex-col min-h-0">
          <CardHeader className="flex-shrink-0 pb-4">
            <CardTitle className="text-lg sm:text-xl">Messages</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (conversationsWithMessages.length === 0) {
    return (
      <div className="w-full h-full flex flex-col">
        <Card className="flex-1 flex flex-col min-h-0">
          <CardHeader className="flex-shrink-0 pb-4">
            <CardTitle className="text-lg sm:text-xl">Messages</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-center justify-center">
            <p className="text-gray-600 text-center text-sm sm:text-base">
              No conversations yet.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <CardHeader className="flex-shrink-0 pb-3 sm:pb-4">
          <CardTitle className="text-lg sm:text-xl">Messages</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 min-h-0 p-3 sm:p-6">
          <ScrollArea className="h-full pr-2 sm:pr-4">
            <div className="space-y-3 sm:space-y-4">
              {conversationsWithMessages.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={selectedConversationId === conversation.id}
                  onSelect={() => onSelectConversation(conversation)}
                />
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// Removed ConversationWithMessagesCheck - we now show ALL conversations
